import { useEffect, useRef } from "react";

import type {
  Candidato,
  TransacaoResumo,
} from "../../lib/conciliacao/tipos";
import { formatarDataISO } from "../../lib/formato/data";
import { formatarDecimal } from "../../lib/formato/decimal";
import { exibirCnpjMascarado } from "./cnpj";
import { formatarScore } from "./score";

/*
 * Passo de confirmação EXPLÍCITO da conciliação manual (FE-3 CP-C — ESCRITA).
 *
 * Esta é a primeira mutação do frontend que MOVE DINHEIRO (cria o movimento de
 * conciliação, o D+0). Regra de UX dura: a escrita NUNCA é disparada por um clique
 * único na fila. O operador clica "Confirmar" num candidato → abre ESTE passo com
 * o RESUMO do que será conciliado (transação ⟷ título escolhido) → só o
 * "Confirmar" DESTE modal dispara o POST. Cancelar fecha sem efeito.
 *
 * Acessibilidade: modal `role="dialog"` + `aria-modal`, rotulado por `aria-labelledby`,
 * com foco movido para o "Confirmar" ao abrir e Escape para cancelar.
 *
 * Componente PURO de apresentação: não conhece o hook de mutação. O orquestrador
 * (`FilaConciliacao`) injeta `aoConfirmar`/`aoCancelar` e o estado `processando`.
 */

interface ConfirmacaoConciliacaoProps {
  transacao: TransacaoResumo;
  candidato: Candidato;
  /** Dispara a mutação real (POST). Só o Confirmar DESTE modal o chama. */
  aoConfirmar: () => void;
  /** Fecha o passo sem efeito de escrita. */
  aoCancelar: () => void;
  /** Mutação em voo: desabilita o Confirmar e troca o rótulo para "Processando…". */
  processando: boolean;
}

export function ConfirmacaoConciliacao({
  transacao,
  candidato,
  aoConfirmar,
  aoCancelar,
  processando,
}: ConfirmacaoConciliacaoProps): JSX.Element {
  const confirmarRef = useRef<HTMLButtonElement>(null);

  // Move o foco para o Confirmar ao abrir (acessibilidade de modal).
  useEffect(() => {
    confirmarRef.current?.focus();
  }, []);

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="titulo-confirmacao-conciliacao"
      onKeyDown={(evento) => {
        if (evento.key === "Escape" && !processando) {
          aoCancelar();
        }
      }}
      className="fixed inset-0 z-50 flex items-center justify-center bg-off-black/50 p-4"
    >
      <div className="flex max-w-lg flex-col gap-4 rounded-md border border-platinum bg-snow p-6 font-corpo text-base text-off-black">
        <h2
          id="titulo-confirmacao-conciliacao"
          className="font-titulo text-xl text-off-black"
        >
          Confirmar conciliação
        </h2>

        <p>
          Conciliar esta transação com este título? Esta ação registra o
          movimento e não pode ser desfeita aqui.
        </p>

        <dl className="flex flex-col gap-3">
          <div className="flex flex-col gap-1">
            <dt className="font-subtitulo">Transação</dt>
            <dd>
              {formatarDecimal(transacao.valor)} ·{" "}
              {formatarDataISO(transacao.data)} ·{" "}
              {transacao.descricao ?? "sem descrição"}
            </dd>
          </div>
          <div className="flex flex-col gap-1">
            <dt className="font-subtitulo">Título</dt>
            <dd>
              {formatarDecimal(candidato.valor)} ·{" "}
              {formatarDataISO(candidato.data_prevista)} ·{" "}
              {exibirCnpjMascarado(candidato.cnpj_masc)} · confiança{" "}
              {formatarScore(candidato.score)}
            </dd>
          </div>
        </dl>

        <div className="flex justify-end gap-3">
          <button
            type="button"
            onClick={aoCancelar}
            disabled={processando}
            className="rounded-md border border-platinum bg-snow px-4 py-1 text-off-black disabled:opacity-60"
          >
            Cancelar
          </button>
          <button
            ref={confirmarRef}
            type="button"
            onClick={aoConfirmar}
            disabled={processando}
            className="rounded-md border border-flame bg-flame px-4 py-1 text-snow disabled:opacity-60"
          >
            {processando ? "Processando…" : "Confirmar"}
          </button>
        </div>
      </div>
    </div>
  );
}
